const HEALTH_URL = "/up"
const PING_TIMEOUT = 5000        // 5s — heartbeat request timeout
const HEARTBEAT_INTERVAL = 15000 // 15s — check interval (only when offline)

export class NetworkStatus {
  constructor() {
    this.listeners = []
    this._checking = false
    this._heartbeatId = null

    // Restore offline state from previous page navigation
    const wasOffline = sessionStorage.getItem("networkOffline") === "1"
    const browserOffline = !navigator.onLine

    if (wasOffline || browserOffline) {
      this._online = false
      this.startHeartbeat()
    } else {
      this._online = true
    }

    // Browser offline event as fast hint (airplane mode)
    window.addEventListener("offline", () => this.goOffline())
    window.addEventListener("online", () => {
      // Don't trust browser "online" — verify with heartbeat
      this.check()
    })
  }

  get online() {
    return this._online
  }

  onChange(callback) {
    this.listeners.push(callback)
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback)
    }
  }

  // Transition to offline: show banner, start heartbeat
  goOffline() {
    if (!this._online) return // already offline
    this._online = false
    this.notify()
    this.startHeartbeat()
  }

  // Transition to online: hide banner, stop heartbeat, trigger sync
  goOnline() {
    if (this._online) return // already online
    this._online = true
    this.stopHeartbeat()
    this.notify()
  }

  notify() {
    // Persist state so next page navigation starts with correct status
    sessionStorage.setItem("networkOffline", this._online ? "0" : "1")
    this.listeners.forEach(cb => cb(this._online))
    // Tell SW to switch caching strategy (cache-first when offline, network-first when online)
    this.notifySW()
  }

  notifySW() {
    if (!navigator.serviceWorker?.controller) return
    navigator.serviceWorker.controller.postMessage({
      type: this._online ? "ONLINE" : "OFFLINE"
    })
  }

  // Heartbeat: ping /up with 5s timeout
  async check() {
    if (this._checking) return
    this._checking = true

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT)

      const response = await fetch(HEALTH_URL, {
        method: "HEAD",
        cache: "no-store",
        signal: controller.signal
      })
      clearTimeout(timeoutId)

      if (response.ok) {
        this.goOnline()
      } else {
        this.goOffline()
      }
    } catch {
      this.goOffline()
    } finally {
      this._checking = false
    }
  }

  startHeartbeat() {
    if (this._heartbeatId) return // already running
    this._heartbeatId = setInterval(() => this.check(), HEARTBEAT_INTERVAL)
  }

  stopHeartbeat() {
    if (this._heartbeatId) {
      clearInterval(this._heartbeatId)
      this._heartbeatId = null
    }
  }

  // Called from api_helper when any request times out or network error
  reportFailure() {
    this.goOffline()
  }

  // Called from api_helper when a request succeeds
  reportSuccess() {
    // Request succeeded — we're definitely online, no need for heartbeat
    if (!this._online) {
      this.goOnline()
    }
  }
}

export const networkStatus = new NetworkStatus()
